import React, { Fragment } from 'react'
import styled from 'styled-components'
import isEmpty from 'lodash/isEmpty'
import Filter from '../../components/Filter/Filter'
import Sort from '../../components/Sort/Sort'
import { SCREEN_SIZE, withMedia, Media } from '../../lib/Media'
import Router, { withRouter } from 'next/router'
import {
  mapFiltersFromUrl,
  generateQueryString,
  convertFilters,
} from '../../lib/filterFormatter'
import TagCollection from '../../components/TagCollection/TagCollection'
import GlobalContext from '../GlobalContext'
import { EventTrackingConsumer } from '../EventTrackingProvider/EventTrackingProvider'
import { AMP_EVENT_NAME, CTA_LOCATION_NAME } from '../../lib/amplitude'
import { GTM_ENV_VAR as GEV } from '../../lib/eventTrackerFunction'
import { getAllFilterCount } from '../../utils/Utils'
import { encodeSlugIfExist, isEncodedURIComponent } from '../../lib/utils'
import { CheckoutAddressConsumer } from '../../components/CheckoutAddressProvider'
import { trackFilterAppliedEvent } from '../../lib/analytics/events/filter/FilterAppliedEvent'
import { trackSortAppliedEvent } from '../../lib/analytics/events/sort/SortAppliedEvent'
import { createFilterAppliedEventParams } from '../../lib/analytics/utils/Filter/FilterEventHelper'
import { createSortAppliedEventParams } from '../../lib/analytics/utils/Sort/SortEventHelper'

const FilterAndSortContainer = styled.div`
  display: flex;
  margin: 1.125rem 0rem;
  ${SCREEN_SIZE.Only.Tablet} {
    margin: 0 0 1.125rem;
  }
  ${SCREEN_SIZE.From.Desktop} {
    flex-direction: column;
    margin: 0rem;
    > div:not(:last-child) {
      margin-bottom: 0.5rem;
    }
  }
  ${SCREEN_SIZE.Only.Mobile} {
    margin-top: 0.5rem;
    margin-bottom: 1rem;
  }
`
class FilterAndSort extends React.Component {
  constructor(props) {
    super(props)
    const { query } = props.router || {}

    this.state = {
      filters: [],
      showTooltip: true,
      appliedFilters: mapFiltersFromUrl(query?.filter),
      sortType: query?.sorting,
      query: query?.query,
      selectedTag: query?.tag,
    }
    this.handleApplyFilter = this.handleApplyFilter.bind(this)
    this.handleSortType = this.handleSortType.bind(this)
    this.handleClickTag = this.handleClickTag.bind(this)
    this.changeRoute = this.changeRoute.bind(this)
    this.handleTrackFilterAppliedEvent =
      this.handleTrackFilterAppliedEvent.bind(this)
    this.handleTrackSortAppliedEvent =
      this.handleTrackSortAppliedEvent.bind(this)
  }

  handleClickTag(tag) {
    this.setState({ selectedTag: tag }, () => {
      this.changeRoute({}, '', tag)
      if (this.props.ctaLocation?.pageName === CTA_LOCATION_NAME.PROMOTION) {
        this.props.track({
          [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.PROMO_CLICKED,
          [GEV.AMPLITUDE_CTA_LOCATION]: this.props.ctaLocation,
          [GEV.AMPLITUDE_MESSAGE]: tag,
        })
      }
    })
  }

  handleSortType(sortType, sortLabel) {
    this.setState({ sortType }, () => {
      this.changeRoute(
        this.state.appliedFilters,
        sortType,
        this.state.selectedTag
      )
    })

    const query = this.props.router?.query || {}
    this.props.track({
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.SORT_BY_SELECTED,
      [GEV.AMPLITUDE_CTA_LOCATION]: {
        pageName: CTA_LOCATION_NAME.PRODUCT_LISTING,
        pageMeta: [query?.pageType],
      },
      [GEV.AMPLITUDE_SORT_BY_CATEGORY]: sortLabel?.toLowerCase(),
    })
    this.handleTrackSortAppliedEvent(sortLabel)
  }

  handleApplyFilter(filters, selectedFilter) {
    const { track, metaData } = this.props
    this.setState({ appliedFilters: filters }, () => {
      this.changeRoute(filters, this.state.sortType, this.state.selectedTag)
    })
    // only track if filter is applied
    if (!selectedFilter?.checked) {
      const query = this.props.router?.query || {}
      const data = {
        [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.FILTER_SELECTED,
        [GEV.AMPLITUDE_CTA_LOCATION]: {
          pageName: this.props.ctaLocation?.pageName,
          pageMeta: [query?.pageType],
        },
        [GEV.AMPLITUDE_FILTER_CATEGORY]:
          selectedFilter?.category?.toLowerCase(),
        [GEV.AMPLITUDE_FILTER_DETAILS]: selectedFilter?.label?.toLowerCase(),
      }
      if (metaData?.queryId) {
        data[GEV.AMPLITUDE_FILTERS] = [
          `${selectedFilter?.category?.toLowerCase()}: ${selectedFilter?.label?.toLowerCase()}`,
        ]
        data[GEV.AMPLITUDE_EVENT_TYPE] = 'click'
        data[GEV.AMPLITUDE_EVENT_SCOPE] = 3
        data[GEV.AMPLITUDE_SEARCH_QUERY_ID] = metaData?.queryId
        data[GEV.AMPLITUDE_SEARCH_INDEX_NAME] = metaData?.indexName
        data[GEV.PRODUCT_POSITION] = []
      }
      track(data)
      this.handleTrackFilterAppliedEvent(selectedFilter)
    }
  }

  handleTrackSortAppliedEvent(sortLabel) {
    if (!sortLabel) {
      return
    }
    const { checkoutAddress } = this.props
    trackSortAppliedEvent(
      createSortAppliedEventParams(sortLabel, checkoutAddress)
    )
  }

  handleTrackFilterAppliedEvent(selectedFilter) {
    if (!selectedFilter) {
      return
    }
    const { checkoutAddress } = this.props
    trackFilterAppliedEvent(
      createFilterAppliedEventParams(selectedFilter, checkoutAddress)
    )
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps?.router?.query?.filter !== this?.props?.router?.query?.filter
    ) {
      this.setState({
        appliedFilters: mapFiltersFromUrl(this.props?.router?.query?.filter),
      })
    }
    if (
      prevProps?.router?.query?.sorting !== this?.props.router?.query?.sorting
    ) {
      this.setState({ sortType: this.props?.router?.query?.sorting })
    }
    if (prevProps?.router?.query?.tag !== this?.props.router?.query?.tag) {
      this.setState({ selectedTag: this.props?.router?.query?.tag })
    }
  }

  changeRoute(filters, sortType, tag) {
    let href = this.props.router.pathname
    let asPath = this.props.router.asPath.split('?')[0]
    const queryStringObject = {}

    if (this.props.router.query && this.props.router.query.query) {
      queryStringObject.query = encodeURIComponent(
        this.props.router.query.query
      )
    }

    if (filters && Object.keys(filters).length) {
      queryStringObject.filter = convertFilters(filters)
    }

    if (sortType) {
      queryStringObject.sorting = sortType
    }

    if (tag) {
      queryStringObject.tag = tag
    }

    asPath += generateQueryString(queryStringObject)

    queryStringObject.slug = isEncodedURIComponent(this.props.router.query.slug)
      ? this.props.router.query.slug
      : encodeSlugIfExist(this.props.router.query.slug)
    href += generateQueryString(queryStringObject)

    Router.push(href, asPath)
  }

  render() {
    const { filters, filterExcept, isEnabledApiExperiment, tagCollection } =
      this.props

    let filterAndSortHtml = ''
    if (!isEmpty(tagCollection)) {
      filterAndSortHtml = (
        <TagCollection
          tagCollection={tagCollection}
          onClickTag={this.handleClickTag}
        />
      )
    } else if (!isEmpty(filters)) {
      filterAndSortHtml = (
        <Fragment>
          <Media query={SCREEN_SIZE.Only.Tablet}>
            <FilterAndSortContainer>
              <Filter
                filterCount={getAllFilterCount(this.state.appliedFilters)}
                data={filters}
                onApplyFilters={this.handleApplyFilter}
                except={filterExcept}
                isEnabledApiExperiment={isEnabledApiExperiment}
              />
              <Sort onHandleSort={this.handleSortType} />
            </FilterAndSortContainer>
          </Media>
          <Media query={SCREEN_SIZE.Only.Mobile}>
            <FilterAndSortContainer>
              <Filter
                filterCount={getAllFilterCount(this.state.appliedFilters)}
                data={filters}
                onApplyFilters={this.handleApplyFilter}
                except={filterExcept}
                isEnabledApiExperiment={isEnabledApiExperiment}
              />
              <Sort onHandleSort={this.handleSortType} />
            </FilterAndSortContainer>
          </Media>
          <Media query={SCREEN_SIZE.From.Desktop}>
            <FilterAndSortContainer>
              <Sort onHandleSort={this.handleSortType} />
              <Filter
                filterCount={getAllFilterCount(this.state.appliedFilters)}
                data={filters}
                onApplyFilters={this.handleApplyFilter}
                except={filterExcept}
                isEnabledApiExperiment={isEnabledApiExperiment}
              />
            </FilterAndSortContainer>
          </Media>
        </Fragment>
      )
    }

    return filterAndSortHtml
  }
}

FilterAndSort.defaultProps = {
  isEnabledApiExperiment: false,
}

const FilterAndSortWrapper = props => (
  <GlobalContext.Consumer>
    {({ remoteConfig }) => (
      <EventTrackingConsumer>
        {({ track }) => (
          <CheckoutAddressConsumer>
            {({ checkoutAddress }) => (
              <FilterAndSort
                {...props}
                remoteConfig={remoteConfig}
                track={track}
                checkoutAddress={checkoutAddress}
              />
            )}
          </CheckoutAddressConsumer>
        )}
      </EventTrackingConsumer>
    )}
  </GlobalContext.Consumer>
)

export default withMedia(withRouter(FilterAndSortWrapper))
